import React, { useState, useEffect } from 'react';
import proxyAxiosInstance, { createProxyInstance } from './utils/proxyAxiosConfig';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  Code,
  VStack,
  Spinner
} from '@chakra-ui/react';

interface ProxyExampleProps {
  baseProxyEndpoint: string;
}

interface ProxyResponse {
  data: any;
  loading: boolean;
  error: string | null;
}

export const ProxyExample: React.FC<ProxyExampleProps> = ({ baseProxyEndpoint }) => {
  // Create a custom proxy instance with the provided base URL and default API key
  const customProxyInstance = createProxyInstance(baseProxyEndpoint);
  
  const [response, setResponse] = useState<ProxyResponse>({
    data: null,
    loading: false,
    error: null,
  });

  async function getCapitalOfSpain() {
    // The API key is automatically included in all requests by the proxy instance
    const url = '/v1/chat/completions';
  
    // Ensure data is properly formatted as JSON
    const data = {
        model: 'gpt-4', // Specifies the use of GPT-4
        messages: [
            { role: 'user', content: 'Hi, tell me the capital of Spain.' }
        ],
        max_tokens: 50, // Adjust as necessary
        temperature: 0.7,
    };
    
    // Set loading state
    setResponse(prev => ({ ...prev, loading: true, error: null }));
    
    try {
        // Log the request data for debugging
        console.log('Sending request to OpenAI with data:', JSON.stringify(data));
        
        // Set explicit headers to ensure content type is properly set
        const response = await customProxyInstance.post(url, data, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        const messageContent = response.data.choices[0]?.message?.content || 'No response content';
        console.log('Response from OpenAI:', messageContent);
        
        // Update UI with successful response
        setResponse({
          data: response.data,
          loading: false,
          error: null,
        });
    } catch (error: any) {
        console.error('Error making API request:', error);
        
        // Update UI with error message
        let errorMessage = 'Unknown error occurred';
        
        // Vapor's Abort returns: { error: true, reason: "..." }
        if (error.response?.data?.reason) {
          errorMessage = error.response.data.reason;
        } else if (error.response?.data?.error) {
          // Handle case where error.response.data.error is an object
          if (typeof error.response.data.error === 'object') {
            errorMessage = error.response.data.error.message || JSON.stringify(error.response.data.error);
          } else {
            errorMessage = error.response.data.error;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
        
        setResponse({
          data: null,
          loading: false,
          error: errorMessage,
        });
    }
  }

  const fetchData = async () => {
    setResponse(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await customProxyInstance.get(baseProxyEndpoint);
      setResponse({
        data: result.data,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error making API request:', error);
      
      // Update UI with error message
      let errorMessage = 'Unknown error occurred';
      
      // Vapor's Abort returns: { error: true, reason: "..." }
      if (error.response?.data?.reason) {
        errorMessage = error.response.data.reason;
      } else if (error.response?.data?.error) {
        // Handle case where error.response.data.error is an object
        if (typeof error.response.data.error === 'object') {
          errorMessage = error.response.data.error.message || JSON.stringify(error.response.data.error);
        } else {
          errorMessage = error.response.data.error;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setResponse({
        data: null,
        loading: false,
        error: errorMessage,
      });
    }
  };

  return (
    <Container maxW="container.lg" py={8}>
      <VStack gap={6} alignItems="stretch">
        <Box>
          <Heading as="h2" size="lg" mb={2}>Proxy Example</Heading>
          <Text mb={4}>This component demonstrates using the authenticating proxy to make requests to: <Code>{baseProxyEndpoint}</Code></Text>
          
          <Button
            colorScheme="blue"
            onClick={getCapitalOfSpain}
            isLoading={response.loading}
            loadingText="Loading..."
            mb={6}
          >
            Fetch Data via Proxy
          </Button>
        </Box>
        
        {response.error && (
          <Box p={4} bg="red.50" color="red.700" borderRadius="md" borderWidth="1px" borderColor="red.200">
            <Text fontWeight="medium">{typeof response.error === 'string' ? response.error : JSON.stringify(response.error)}</Text>
          </Box>
        )}
        
        {response.data && (
          <Box p={4} borderRadius="md" bg="gray.50">
            <Heading as="h3" size="md" mb={2}>Response Data:</Heading>
            <Box 
              as="pre"
              p={3}
              borderRadius="md"
              bg="gray.100"
              whiteSpace="pre-wrap"
              fontSize="sm"
              overflowX="auto"
            >
              {JSON.stringify(response.data, null, 2)}
            </Box>
          </Box>
        )}
      </VStack>
    </Container>
  );
};